import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PageHeader, DataCheckChip, KpiCard } from '../components/ds';
const toneFromColor = (c = '') => c.includes('emerald') || c.includes('green') ? 'success' : c.includes('rose') || c.includes('red') ? 'danger' : c.includes('amber') || c.includes('orange') ? 'warning' : c.includes('violet') || c.includes('purple') || c.includes('indigo') ? 'primary' : c.includes('teal') || c.includes('cyan') || c.includes('blue') ? 'teal' : 'default';

import { toast } from 'sonner';
import { 
  Users, TrendingUp, CheckCircle, Calendar, Filter,
  ArrowRight, Phone, Mail, UserCheck, Target, Award, ExternalLink
} from 'lucide-react';
import DateRangeFilter from '../components/DateRangeFilter';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import ViewLeadsModal from '../components/ViewLeadsModal';
import { getApiUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const API_URL = getApiUrl();

const PreSalesDashboard = () => {
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [viewLeadsModal, setViewLeadsModal] = useState(null);
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedSource, setSelectedSource] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [users, setUsers] = useState([]);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    fetchUserRole();
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUserRole = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      // API returns array directly, not {users: [...]}
      const allUsers = Array.isArray(response.data) ? response.data : (response.data.users || []);
      const presalesUsers = allUsers.filter(u => u.role === 'pre_sales');
      setUsers(presalesUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedUser) params.append('user_id', selectedUser);
      if (selectedSource.length) params.append('source', selectedSource.join(','));
      if (selectedCountry.length) params.append('country', selectedCountry.join(','));

      const response = await axios.get(
        `${API_URL}/api/analytics/presales-funnel?${params.toString()}`,
        { withCredentials: true }
      );
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchStats();
  };

  const openPresalesLeadsModal = (title, metric) => {
    setViewLeadsModal({
      title,
      filters: {
        metric,
        start_date: startDate,
        end_date: endDate,
        apiBase: '/api/analytics/presales-dashboard/leads',
        user_id: selectedUser,
      },
    });
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <KpiCard title={title} value={value} icon={icon} tone={toneFromColor(color)} subtitle={subtitle}
      testId={`stat-card-${String(title).toLowerCase().replace(/\s+/g, '-')}`} />
  );

  const FunnelStep = ({ title, count, percentage, isLast, color, onViewLeads }) => (
    <div className="flex items-center">
      <div className={`flex-1 p-4 rounded-lg ${color} text-center`}>
        <p className="text-sm font-medium text-slate-600">{title}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{count}</p>
        {percentage !== null && (
          <p className="text-xs text-slate-500 mt-1">{percentage}%</p>
        )}
        {onViewLeads && count > 0 && (
          <button
            onClick={onViewLeads}
            className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-1 mx-auto"
          >
            View Leads <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>
      {!isLast && (
        <ArrowRight className="w-6 h-6 text-slate-300 mx-2 flex-shrink-0" />
      )}
    </div>
  );

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="presales-dashboard">
      <PageHeader
        title="Pre-Sales Dashboard"
        description="Track lead funnel and performance metrics"
        actions={<DataCheckChip startDate={startDate || undefined} endDate={endDate || undefined} />}
      />

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangeFilter
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            label="Date Range"
          />
          {(userRole === 'super_admin' || userRole === 'finance_admin') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Pre-Sales Associate</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="input-base min-w-[200px]"
                data-testid="user-filter"
              >
                <option value="">All Associates</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>{user.full_name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Lead Source</label>
            <MultiSelectDropdown
              options={[
                { value: 'manual', label: 'Manual (Added by team)' },
                { value: 'auto_imported', label: 'Auto-Imported' },
              ]}
              selected={selectedSource}
              onChange={setSelectedSource}
              placeholder="All Sources"
              testId="source-filter"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
            <MultiSelectDropdown
              options={[
                { value: 'India', label: 'India' },
                { value: 'US', label: 'US' },
                { value: 'UK', label: 'UK' },
                { value: 'Other', label: 'Other' },
              ]}
              selected={selectedCountry}
              onChange={setSelectedCountry}
              placeholder="All Countries"
              testId="country-filter"
            />
          </div>
          <button
            onClick={handleFilter}
            className="btn-primary flex items-center gap-2"
            data-testid="apply-filter"
          >
            <Filter className="w-4 h-4" />
            Apply Filter
          </button>
        </div>
      </div>

      {stats && (
        <>
          {/* Funnel Visualization */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Lead Funnel</h2>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <FunnelStep 
                title="Leads Added" 
                count={stats.funnel.total_leads_added}
                percentage={null}
                color="bg-blue-50"
                onViewLeads={() => openPresalesLeadsModal('Leads Added', 'leads_added')}
              />
              <FunnelStep 
                title="Trial Paid" 
                count={stats.funnel.trial_paid}
                percentage={stats.funnel.total_leads_added > 0 ? Math.round(stats.funnel.trial_paid / stats.funnel.total_leads_added * 100) : 0}
                color="bg-violet-50"
                onViewLeads={() => openPresalesLeadsModal('Trial Paid', 'trial_paid')}
              />
              <FunnelStep 
                title="Trial Booked" 
                count={stats.funnel.trial_booked}
                percentage={stats.funnel.trial_paid > 0 ? Math.round(stats.funnel.trial_booked / stats.funnel.trial_paid * 100) : 0}
                color="bg-amber-50"
                onViewLeads={() => openPresalesLeadsModal('Trial Booked', 'trial_booked')}
              />
              <FunnelStep 
                title="Trial Completed" 
                count={stats.funnel.trial_completed}
                percentage={stats.funnel.trial_booked > 0 ? Math.round(stats.funnel.trial_completed / stats.funnel.trial_booked * 100) : 0}
                color="bg-emerald-50"
                onViewLeads={() => openPresalesLeadsModal('Trial Completed', 'trial_completed')}
              />
              <FunnelStep 
                title="Consultation Done" 
                count={stats.funnel.consultation_completed}
                percentage={stats.funnel.trial_completed > 0 ? Math.round(stats.funnel.consultation_completed / stats.funnel.trial_completed * 100) : 0}
                color="bg-teal-50"
                onViewLeads={() => openPresalesLeadsModal('Consultation Done', 'consultation_done')}
              />
              <FunnelStep 
                title="Enrolled" 
                count={stats.funnel.enrolled}
                percentage={stats.funnel.consultation_completed > 0 ? Math.round(stats.funnel.enrolled / stats.funnel.consultation_completed * 100) : 0}
                color="bg-green-50"
                isLast
                onViewLeads={() => openPresalesLeadsModal('Enrolled', 'enrolled')}
              />
            </div>
          </div>

          {/* Lead Status Funnel with Links */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Lead Status Breakdown</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
              {[
                { status: 'new', label: 'New', color: 'bg-blue-100 text-blue-700', count: stats.lead_status_breakdown?.new || 0, metric: 'status_new' },
                { status: 'wip', label: 'WIP', color: 'bg-indigo-100 text-indigo-700', count: stats.lead_status_breakdown?.wip || 0, metric: 'status_contacted' },
                { status: 'callback_scheduled', label: 'Callback', color: 'bg-amber-100 text-amber-700', count: stats.lead_status_breakdown?.callback_scheduled || 0, metric: 'status_callback' },
                { status: 'follow_up', label: 'Follow Up', color: 'bg-purple-100 text-purple-700', count: stats.lead_status_breakdown?.follow_up || 0, metric: 'status_follow_up' },
                { status: 'dnp', label: 'DNP', color: 'bg-orange-100 text-orange-700', count: stats.lead_status_breakdown?.dnp || 0, metric: 'status_dnp' },
                { status: 'no_show', label: 'No-Show', color: 'bg-cyan-100 text-cyan-700', count: stats.lead_status_breakdown?.no_show || 0, metric: 'status_no_show' },
                { status: 'converted', label: 'Converted', color: 'bg-emerald-100 text-emerald-700', count: stats.lead_status_breakdown?.converted || 0, metric: 'status_converted' },
                { status: 'not_interested', label: 'Not Interested', color: 'bg-red-100 text-red-700', count: stats.lead_status_breakdown?.not_interested || 0, metric: 'status_not_interested' },
              ].map((item) => (
                <div 
                  key={item.status}
                  className={`${item.color} rounded-xl p-4 text-center cursor-pointer hover:opacity-80 transition-opacity`}
                  onClick={() => openPresalesLeadsModal(item.label, item.metric)}
                  data-testid={`presales-status-card-${item.status}`}
                >
                  <div className="text-2xl font-bold">{item.count}</div>
                  <div className="text-sm font-medium">{item.label}</div>
                  <button className="mt-2 text-xs flex items-center justify-center gap-1 hover:underline opacity-70">
                    <ExternalLink className="w-3 h-3" /> View Leads
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Leads → Trial Booked"
              value={`${stats.funnel.total_leads_added > 0 ? Math.round(stats.funnel.trial_booked / stats.funnel.total_leads_added * 100) : 0}%`}
              icon={TrendingUp}
              color="bg-blue-500"
              subtitle="Conversion rate"
            />
            <StatCard
              title="Trial Booked → Taken"
              value={`${stats.conversion_rates.trial_booked_to_taken_pct}%`}
              icon={CheckCircle}
              color="bg-emerald-500"
              subtitle="Conversion rate"
            />
            <StatCard
              title="Trial Booked → Consultation"
              value={`${stats.conversion_rates.trial_booked_to_consultation_pct}%`}
              icon={Target}
              color="bg-violet-500"
              subtitle="Conversion rate"
            />
            <StatCard
              title="Consultation → Enrollment"
              value={`${stats.conversion_rates.consultation_to_enrollment_pct}%`}
              icon={Award}
              color="bg-amber-500"
              subtitle="Conversion rate"
            />
          </div>

          {/* User Breakdown (Admin only) */}
          {stats.user_breakdown && stats.user_breakdown.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Pre-Sales Leaderboard</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-slate-500 border-b border-slate-200">
                      <th className="pb-3 font-medium">Associate</th>
                      <th className="pb-3 font-medium text-center">Leads</th>
                      <th className="pb-3 font-medium text-center">Trial Paid</th>
                      <th className="pb-3 font-medium text-center">Booked</th>
                      <th className="pb-3 font-medium text-center">Completed</th>
                      <th className="pb-3 font-medium text-center">Consultation</th>
                      <th className="pb-3 font-medium text-center">Enrolled</th>
                      <th className="pb-3 font-medium text-center">Booked→Taken</th>
                      <th className="pb-3 font-medium text-center">Booked→Consult</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.user_breakdown.map((user, idx) => (
                      <tr key={user.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            {idx === 0 && <span className="text-amber-500">🥇</span>}
                            {idx === 1 && <span className="text-slate-400">🥈</span>}
                            {idx === 2 && <span className="text-amber-700">🥉</span>}
                            <span className="font-medium text-slate-900">{user.full_name}</span>
                          </div>
                        </td>
                        <td className="py-3 text-center text-slate-600">{user.total_leads}</td>
                        <td className="py-3 text-center text-slate-600">{user.trial_paid}</td>
                        <td className="py-3 text-center text-slate-600">{user.trial_booked}</td>
                        <td className="py-3 text-center text-slate-600">{user.trial_completed}</td>
                        <td className="py-3 text-center text-slate-600">{user.consultation_completed}</td>
                        <td className="py-3 text-center font-semibold text-emerald-600">{user.enrolled}</td>
                        <td className="py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.booked_to_taken_pct >= 70 ? 'bg-emerald-100 text-emerald-700' :
                            user.booked_to_taken_pct >= 50 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {user.booked_to_taken_pct}%
                          </span>
                        </td>
                        <td className="py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.booked_to_consultation_pct >= 60 ? 'bg-emerald-100 text-emerald-700' :
                            user.booked_to_consultation_pct >= 40 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {user.booked_to_consultation_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {viewLeadsModal && (
        <ViewLeadsModal
          title={viewLeadsModal.title}
          filters={viewLeadsModal.filters}
          onClose={() => setViewLeadsModal(null)}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default PreSalesDashboard;
